import React, { useState, useEffect, useRef } from 'react';
import { useDashboard } from './DashboardContext';
import { coachingApi } from '../../services/api';
import useVisiblePolling from '../../hooks/useVisiblePolling';
import { 
  Lock, 
  MessageSquare, 
  Send, 
  ToggleLeft, 
  ToggleRight,
  Star,
  ChevronDown,
  ChevronUp,
  Play,
  CheckCircle,
  Loader2,
  AlertCircle,
  Calendar
} from 'lucide-react';
import './MyCoachPage.css';

const MyCoachPage = () => {
  const { user, setActivePage } = useDashboard();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [expandedSession, setExpandedSession] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadCoachingData();
  }, []);

  // Poll for new messages every 15s while the tab is visible. Picks up
  // Telegram swipe-replies from the coach without a page refresh.
  useVisiblePolling(async () => {
    const data = await coachingApi.getMyMessages();
    if (!data?.messages) return;
    setMessages(prev => {
      if (data.messages.length === prev.length) {
        const prevIds = new Set(prev.map(m => m.id));
        const hasNew = data.messages.some(m => !prevIds.has(m.id));
        if (!hasNew) return prev;
      }
      return data.messages;
    });
  }, { intervalMs: 15000, enabled: !!status?.has_access, deps: [status?.has_access] });

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadCoachingData = async () => {
    try {
      setLoading(true);
      const statusData = await coachingApi.getMyStatus();
      setStatus(statusData);

      if (statusData.has_access) {
        const [messagesData, sessionsData] = await Promise.all([
          coachingApi.getMyMessages(),
          coachingApi.getMySessions()
        ]);
        setMessages(messagesData.messages || []);
        setSessions(sessionsData.sessions || []);
        setSchedule(sessionsData.schedule);
      }
    } catch (error) {
      console.error('Failed to load coaching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSharing = async () => {
    if (toggling) return;
    setToggling(true);
    try {
      const result = await coachingApi.toggleSharing(!status.sharing_enabled);
      setStatus(prev => ({ ...prev, sharing_enabled: result.sharing_enabled }));
    } catch (error) {
      console.error('Failed to toggle sharing:', error);
      alert('Failed to update sharing preference');
    } finally {
      setToggling(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const message = await coachingApi.sendMessageToCoach(newMessage.trim());
      setMessages(prev => [...prev, message]);
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="mycoach-page">
        <div className="mycoach-loading">
          <Loader2 className="spin" size={32} />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Locked state for non-paid members
  if (!status?.has_access) {
    return (
      <div className="mycoach-page">
        <div className="mycoach-locked">
          <div className="locked-icon">
            <Lock size={48} />
          </div>
          <h2>Upgrade to Unlock</h2>
          <p>Get direct access to Coach CJ!</p>
          <ul className="locked-features">
            <li>✓ Share your performance for personalized tips</li>
            <li>✓ Message Coach CJ directly</li>
            <li>✓ Get feedback on your trading journey</li>
          </ul>
          <button 
            className="upgrade-btn"
            onClick={() => setActivePage('Membership')}
            data-testid="upgrade-to-live-trading-btn"
          >
            Upgrade to Live Trading
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mycoach-page">
      <div className="mycoach-header">
        <h1>👨‍🏫 My Coach</h1>
        <p>Connect with Coach CJ for personalized guidance</p>
      </div>

      {/* Sharing Toggle Section */}
      <div className="sharing-section">
        <div className="sharing-card">
          <div className="sharing-content">
            <h3>Share with Coach CJ</h3>
            <p>
              {status.sharing_enabled 
                ? 'Coach CJ can now view your performance data'
                : 'Enable sharing to let Coach CJ view your trading performance'}
            </p>
            {status.sharing_enabled && (
              <ul className="sharing-list">
                <li>• P/L and trade history</li>
                <li>• Habit scores and streaks</li>
                <li>• Performance analytics</li>
              </ul>
            )}
          </div>
          <button 
            className={`sharing-toggle ${status.sharing_enabled ? 'enabled' : ''}`}
            onClick={handleToggleSharing}
            disabled={toggling}
            data-testid="toggle-sharing-btn"
          >
            {toggling ? (
              <Loader2 className="spin" size={20} />
            ) : status.sharing_enabled ? (
              <>
                <ToggleRight size={24} />
                <span>Sharing Enabled ✓</span>
              </>
            ) : (
              <>
                <ToggleLeft size={24} />
                <span>Enable Sharing</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Chat Section */}
      <div className="chat-section">
        <div className="chat-header">
          <MessageSquare size={20} />
          <h3>Chat with Coach CJ</h3>
          {status.unread_messages > 0 && (
            <span className="unread-badge">{status.unread_messages} new</span>
          )}
        </div>
        
        <div className="chat-messages" data-testid="chat-messages">
          {messages.length === 0 ? (
            <div className="no-messages">
              <p>No messages yet. Start a conversation with Coach CJ!</p>
            </div>
          ) : (
            messages.map((msg, index) => (
              <div 
                key={msg.id || index}
                className={`message ${msg.sender === 'student' ? 'sent' : 'received'}`}
              >
                <div className="message-content">
                  <p>{msg.message}</p>
                  <span className="message-time">{formatTime(msg.timestamp)}</span>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        <form className="chat-input" onSubmit={handleSendMessage}>
          <input
            type="text"
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            disabled={sending}
            data-testid="coach-message-input"
          />
          <button 
            type="submit" 
            disabled={!newMessage.trim() || sending}
            data-testid="send-message-btn"
          >
            {sending ? <Loader2 className="spin" size={18} /> : <Send size={18} />}
          </button>
        </form>
      </div>

      {/* Mentorship Section (if applicable) */}
      {status.is_mentorship_student && (
        <div className="mentorship-section">
          <div className="mentorship-header">
            <Star size={20} className="star-icon" />
            <h3>Mentorship Program</h3>
          </div>
          <p className="mentorship-badge">
            You&apos;ve been selected for 1-on-1 mentorship!
          </p>

          {/* Recurring Schedule */}
          {schedule && (
            <div className="schedule-card">
              <div className="schedule-info">
                <Calendar size={18} />
                <span className="schedule-text">
                  Your sessions: <strong>{['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'][schedule.day_of_week]} at {schedule.time}</strong>
                </span>
              </div>
              {schedule.zoom_link && (
                <a 
                  href={schedule.zoom_link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="zoom-link"
                >
                  <Play size={16} />
                  Join Session
                </a>
              )}
            </div>
          )}

          <div className="sessions-list">
            <h4>Your Sessions</h4>
            {sessions.length === 0 ? (
              <p className="no-sessions">No sessions scheduled yet.</p>
            ) : (
              <>
                {/* Upcoming Sessions */}
                {sessions.filter(s => s.status === 'scheduled' || !s.status).length > 0 && (
                  <div className="sessions-section">
                    <h5 className="sessions-section-title upcoming">
                      <Calendar size={16} />
                      Upcoming
                    </h5>
                    {sessions
                      .filter(s => s.status === 'scheduled' || !s.status)
                      .sort((a, b) => new Date(a.date) - new Date(b.date))
                      .map((session) => (
                        <div key={session.id} className="session-card upcoming">
                          <div 
                            className="session-header"
                            onClick={() => setExpandedSession(
                              expandedSession === session.id ? null : session.id
                            )}
                          >
                            <div className="session-info">
                              <div className="session-datetime">
                                <Calendar size={16} />
                                <span className="session-date">
                                  {new Date(session.date + 'T12:00:00').toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </span>
                                {session.time && (
                                  <span className="session-time">at {session.time} ET</span>
                                )}
                              </div>
                              {session.series_id && (
                                <span className="session-series-badge">
                                  Part of series
                                </span>
                              )}
                            </div>
                            {expandedSession === session.id ? (
                              <ChevronUp size={20} />
                            ) : (
                              <ChevronDown size={20} />
                            )}
                          </div>

                          {expandedSession === session.id && (
                            <div className="session-details">
                              {session.zoom_link && (
                                <a 
                                  href={session.zoom_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="zoom-join-btn"
                                >
                                  <Play size={18} />
                                  Join Meeting
                                </a>
                              )}
                              {!session.zoom_link && (
                                <p className="no-link-note">Meeting link will be added soon</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                )}

                {/* Past Sessions */}
                {sessions.filter(s => s.status === 'completed').length > 0 && (
                  <div className="sessions-section">
                    <h5 className="sessions-section-title past">
                      <CheckCircle size={16} />
                      Past Sessions
                    </h5>
                    {sessions
                      .filter(s => s.status === 'completed')
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((session) => (
                        <div key={session.id} className="session-card past">
                          <div 
                            className="session-header"
                            onClick={() => setExpandedSession(
                              expandedSession === session.id ? null : session.id
                            )}
                          >
                            <div className="session-info">
                              <div className="session-datetime">
                                <Calendar size={16} />
                                <span className="session-date">
                                  {new Date(session.date + 'T12:00:00').toLocaleDateString('en-US', {
                                    month: 'long',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </span>
                              </div>
                            </div>
                            <div className="session-header-right">
                              {session.recording_url && (
                                <span className="has-recording">
                                  <Play size={14} />
                                  Recording
                                </span>
                              )}
                              {expandedSession === session.id ? (
                                <ChevronUp size={20} />
                              ) : (
                                <ChevronDown size={20} />
                              )}
                            </div>
                          </div>

                          {expandedSession === session.id && (
                            <div className="session-details">
                              {session.recording_url && (
                                <a 
                                  href={session.recording_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="recording-btn"
                                >
                                  <Play size={18} />
                                  Watch Recording
                                </a>
                              )}

                              {session.notes && (
                                <div className="session-notes">
                                  <h6>Session Notes</h6>
                                  <p>{session.notes}</p>
                                </div>
                              )}

                              {session.action_items && session.action_items.length > 0 && (
                                <div className="session-actions">
                                  <h6>Action Items</h6>
                                  <ul>
                                    {session.action_items.map((item, idx) => (
                                      <li key={idx}>
                                        <CheckCircle size={14} />
                                        {item}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {!session.recording_url && !session.notes && (!session.action_items || session.action_items.length === 0) && (
                                <p className="no-content-note">Session details will be added by your coach</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyCoachPage;
